import type { GetApiProductsData, ProductKind } from '@/api';
import type { TableSort } from '@/ui';

/*
 * Состояние списка товара - фильтры, поиск, сортировка, страница - живёт в
 * search params маршрута, а не в useState. Так ссылку на отфильтрованный список
 * можно отправить коллеге, а F5 не сбрасывает выбор.
 *
 * В URL пишем только то, что отличается от значения по умолчанию: адрес остаётся
 * читаемым, а «чистый» список выглядит как /goods без хвоста.
 */

export const PAGE_SIZE = 20;

export type KindFilter = ProductKind | 'all';
export type ActivityFilter = 'all' | 'active' | 'inactive';

/** Поля, по которым бэкенд разрешает сортировать (SearchProductsQuery.SortFields). */
export const SORT_FIELDS = ['code', 'name', 'kind', 'group'] as const;
export type SortField = (typeof SORT_FIELDS)[number];

const DEFAULT_SORT: TableSort = { field: 'code', dir: 'asc' };
const DEFAULT_ACTIVITY: ActivityFilter = 'active';

/** Форма search params маршрута: всё необязательное, отсутствие = значение по умолчанию. */
export interface ProductsSearch {
  search?: string;
  kind?: KindFilter;
  /** Группа номенклатуры 1С - наименованием целиком. */
  group?: string;
  activity?: ActivityFilter;
  page?: number;
  sort?: string;
}

/** Разобранное состояние с подставленными значениями по умолчанию. */
export interface ResolvedProductsSearch {
  search: string;
  kind: KindFilter;
  /** '' - все группы. */
  group: string;
  activity: ActivityFilter;
  page: number;
  sort: TableSort;
}

const isKindFilter = (value: unknown): value is KindFilter =>
  value === 'all' ||
  value === 'Starter' ||
  value === 'Enzyme' ||
  value === 'Unspecified';

const isActivityFilter = (value: unknown): value is ActivityFilter =>
  value === 'all' || value === 'active' || value === 'inactive';

const parseSort = (value: unknown): TableSort | undefined => {
  if (typeof value !== 'string') return undefined;

  const [field, dir = 'asc'] = value.split(':');
  const isKnownField = (SORT_FIELDS as ReadonlyArray<string>).includes(field);
  if (!isKnownField || (dir !== 'asc' && dir !== 'desc')) return undefined;

  return { field, dir };
};

export const formatSort = (sort: TableSort): string =>
  `${sort.field}:${sort.dir}`;

/**
 * Приводит сырые query-параметры к нашей форме. Мусор и значения по умолчанию
 * отбрасываются, поэтому в адресной строке остаётся только осмысленное.
 */
export const validateProductsSearch = (
  raw: Record<string, unknown>,
): ProductsSearch => {
  const result: ProductsSearch = {};

  const search = typeof raw.search === 'string' ? raw.search.trim() : '';
  if (search !== '') result.search = search;

  if (isKindFilter(raw.kind) && raw.kind !== 'all') result.kind = raw.kind;

  const group = typeof raw.group === 'string' ? raw.group.trim() : '';
  if (group !== '') result.group = group;

  if (isActivityFilter(raw.activity) && raw.activity !== DEFAULT_ACTIVITY)
    result.activity = raw.activity;

  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) result.page = page;

  const sort = parseSort(raw.sort);
  if (sort && formatSort(sort) !== formatSort(DEFAULT_SORT))
    result.sort = formatSort(sort);

  return result;
};

export const resolveProductsSearch = (
  search: ProductsSearch,
): ResolvedProductsSearch => ({
  search: search.search ?? '',
  kind: search.kind ?? 'all',
  group: search.group ?? '',
  activity: search.activity ?? DEFAULT_ACTIVITY,
  page: search.page ?? 1,
  sort: parseSort(search.sort) ?? DEFAULT_SORT,
});

/** Грань доступности в query-параметр бэкенда: «активные» - его значение по умолчанию. */
export const toActivityQuery = (
  activity: ActivityFilter,
): 'Inactive' | 'All' | undefined => {
  if (activity === 'inactive') return 'Inactive';
  if (activity === 'all') return 'All';
  return undefined;
};

/** Переводит разобранное состояние в query-параметры бэкенда. */
export const toProductsQuery = (
  resolved: ResolvedProductsSearch,
): NonNullable<GetApiProductsData['query']> => {
  const query: NonNullable<GetApiProductsData['query']> = {
    PageNumber: resolved.page,
    PageSize: PAGE_SIZE,
    Sort: formatSort(resolved.sort),
  };

  if (resolved.search !== '') query.Search = resolved.search;
  if (resolved.kind !== 'all') query.Kind = resolved.kind;
  if (resolved.group !== '') query.Group = resolved.group;

  // Бэкенд по умолчанию отдаёт только активные позиции, поэтому «активные» не
  // отправляем, а «снятые» и «все» - явно.
  const activity = toActivityQuery(resolved.activity);
  if (activity) query.Activity = activity;

  return query;
};
